import { cn } from '@talentmesh/ui/lib/utils';
import React, { useEffect, useEffectEvent, useMemo } from 'react';
import { useCountdown, useCounter, useInterval, useToggle } from 'usehooks-ts';

interface TypingTextAnimationProps extends React.HTMLAttributes<HTMLSpanElement> {
    /**
     * must have a stable reference
     */
    texts: string[];
    /**
     * @default false
     */
    spaceAfterCaret?: boolean;
    /**
     * typing speed in ms
     * @default 100
     */
    speed?: number;
    /**
     * pause in seconds after current text is complete
     * @default 3
     */
    rest?: number;
    /**
     * deleting speed in ms, if deletionSpeed is not defined, it will follow typing speed
     */
    deletionSpeed?: number;
}

// configure your initial values here
const initialValues: Pick<Required<TypingTextAnimationProps>, 'rest' | 'speed'> = {
    rest: 3,
    speed: 100,
};

const TypingTextAnimation = ({
    texts,
    rest = initialValues.rest,
    spaceAfterCaret = true,
    speed = initialValues.speed,
    deletionSpeed,
    ...props
}: TypingTextAnimationProps) => {
    const { count: textIndex, increment: nextText, reset: resetTextIndex } = useCounter();
    const currentText = useMemo(() => {
        let result = texts.at(textIndex);

        if (!result) {
            resetTextIndex();
            result = texts.at(0) || '';
        }

        if (spaceAfterCaret) {
            result = `${result} `;
        }

        return result;
    }, [texts, textIndex, resetTextIndex]);
    const { count: charIndex, increment: nextChar, decrement: popChar } = useCounter(0);
    const [isStart, toggleIsStart] = useToggle(true);
    const [restCd, restCdController] = useCountdown({ countStart: rest, countStop: 0 });
    const [isDeleting, , setIsDeleting] = useToggle(false);
    const animatedText = useMemo(() => currentText.substring(0, charIndex), [currentText, charIndex]);

    const getCurrentText = useEffectEvent(() => currentText);

    useEffect(() => {
        restCdController.startCountdown();
    }, [restCdController.startCountdown]);

    useEffect(() => {
        if (charIndex === 0 || charIndex === getCurrentText().length) {
            setIsDeleting(charIndex === getCurrentText().length);
        }

        if (charIndex === 0) {
            nextText();
        }
    }, [charIndex]);

    const restartCd = useEffectEvent(() => {
        restCdController.resetCountdown();
        restCdController.startCountdown();
    });

    useEffect(() => {
        if (animatedText === currentText) {
            if (isStart) toggleIsStart();
            restartCd();
        }
    }, [animatedText, currentText]);

    useInterval(
        () => {
            if (!isStart && restCd > 0) return;

            if (isDeleting) {
                popChar();
            } else {
                nextChar();
            }
        },
        isDeleting ? (deletionSpeed ?? speed) : speed,
    );

    return (
        <span
            {...props}
            className={cn('flex items-stretch self-center text-sm font-normal whitespace-pre', props?.className)}
        >
            {animatedText || ' '}
            <span className="flex w-1 animate-pulse bg-gray-500" />
        </span>
    );
};

export default TypingTextAnimation;
